import React from 'react';
import { Shield, AlertTriangle } from 'lucide-react';
import AdSense from '../components/AdSense';
import { useSEO } from '../hooks/useSEO';

const TaxExemptions: React.FC = () => {
  useSEO({
    title: 'Investimentos Isentos de Imposto de Renda em 2026 (LCI, LCA, CRI, CRA e FIIs) | O Teu Dinheiro',
    description: 'Conheça todos os investimentos e regras de isenção de Imposto de Renda em 2026: LCI, LCA, Debêntures Incentivadas, dividendos de FIIs e vendas até R$ 20 mil.',
    keywords: 'investimentos isentos de imposto de renda 2026, isenção LCI LCA, debêntures incentivadas, isenção 20 mil ações, FIIs isentos',
    url: 'https://www.oteudinheiro.com.br/isencoes'
  });
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-lime-600 to-lime-700 text-white py-20">
        <div className="container-custom text-center">
          <Shield className="w-16 h-16 mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Isenções de IR
          </h1>
          <p className="text-xl md:text-2xl text-lime-100 max-w-3xl mx-auto">
            Conheça os investimentos e situações que são isentos de Imposto de Renda
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Investimentos isentos de IR
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-500">
              <div className="flex items-center mb-4">
                <Shield className="w-8 h-8 text-green-600 mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">Poupança</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Rendimentos da poupança são totalmente isentos de IR,
                independente do valor.
              </p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• 100% isento de IR</li>
                <li>• Sem limite de valor</li>
                <li>• Rendimento de 0,5% + TR</li>
              </ul>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-500">
              <div className="flex items-center mb-4">
                <Shield className="w-8 h-8 text-blue-600 mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">LCI/LCA</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Letras de Crédito Imobiliário e do Agronegócio são
                isentas de IR para pessoas físicas.
              </p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• 100% isento de IR</li>
                <li>• Prazo mínimo de 90 dias</li>
                <li>• Rendimento superior à poupança</li>
              </ul>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-purple-500">
              <div className="flex items-center mb-4">
                <Shield className="w-8 h-8 text-purple-600 mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">FIIs</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Dividendos de Fundos Imobiliários são isentos de IR,
                mas ganho de capital paga IR.
              </p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• Dividendos isentos</li>
                <li>• Ganho de capital paga IR</li>
                <li>• Renda mensal</li>
              </ul>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-orange-500">
              <div className="flex items-center mb-4">
                <Shield className="w-8 h-8 text-orange-600 mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">Ações</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Dividendos de ações são isentos de IR, mas ganho de
                capital paga IR (15% para vendas acima de R$ 20.000/mês).
              </p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• Dividendos isentos</li>
                <li>• Ganho de capital: 15%</li>
                <li>• Isenção até R$ 20.000/mês</li>
              </ul>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-teal-500">
              <div className="flex items-center mb-4">
                <Shield className="w-8 h-8 text-teal-600 mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">Tesouro IPCA</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Tesouro IPCA paga IR sobre o rendimento, mas a
                inflação é compensada, protegendo o poder de compra.
              </p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• Paga IR sobre rendimento</li>
                <li>• Proteção contra inflação</li>
                <li>• Tabela regressiva de IR</li>
              </ul>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-red-500">
              <div className="flex items-center mb-4">
                <AlertTriangle className="w-8 h-8 text-red-600 mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">CDBs</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Certificados de Depósito Bancário pagam IR sobre
                o rendimento, mas geralmente rendem mais que a poupança.
              </p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• Paga IR sobre rendimento</li>
                <li>• Rendimento superior</li>
                <li>• Tabela regressiva de IR</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Ad Section */}
      <section className="py-8">
        <div className="container-custom px-4">
          <AdSense />
        </div>
      </section>

      {/* Tax Table Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Tabela regressiva de IR
          </h2>

          <div className="max-w-4xl mx-auto">
            <div className="bg-gray-50 rounded-xl p-8">
              <p className="text-center text-gray-600 mb-8">
                Para investimentos que pagam IR, a alíquota diminui conforme o tempo de aplicação.
              </p>

              <div className="overflow-x-auto">
                <table className="w-full bg-white rounded-lg shadow">
                  <thead>
                    <tr className="bg-lime-100">
                      <th className="px-6 py-3 text-left text-xs font-medium text-lime-900 uppercase tracking-wider">
                        Prazo de Aplicação
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-lime-900 uppercase tracking-wider">
                        Alíquota de IR
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        Até 180 dias
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        22,5%
                      </td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        De 181 a 360 dias
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        20%
                      </td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        De 361 a 720 dias
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        17,5%
                      </td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        Acima de 720 dias
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        15%
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tips Section */}
      <section className="section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Como aproveitar as isenções
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="tip-box">
              <h3 className="text-xl font-semibold text-secondary-900 mb-3">
                Diversifique com isentos
              </h3>
              <p className="text-secondary-700">
                Inclua investimentos isentos de IR em sua carteira para
                reduzir a carga tributária total.
              </p>
            </div>

            <div className="tip-box">
              <h3 className="text-xl font-semibold text-secondary-900 mb-3">
                Planeje o prazo
              </h3>
              <p className="text-secondary-700">
                Para investimentos tributados, mantenha por mais tempo
                para pagar alíquotas menores de IR.
              </p>
            </div>

            <div className="tip-box">
              <h3 className="text-xl font-semibold text-secondary-900 mb-3">
                Use o limite de ações
              </h3>
              <p className="text-secondary-700">
                Aproveite a isenção de IR para ganhos de capital em
                ações até R$ 20.000 por mês.
              </p>
            </div>

            <div className="tip-box">
              <h3 className="text-xl font-semibold text-secondary-900 mb-3">
                Considere o rendimento líquido
              </h3>
              <p className="text-secondary-700">
                Compare investimentos pelo rendimento após impostos,
                não apenas pela taxa bruta.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-lime-600 text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold mb-6">
            Otimize seus investimentos!
          </h2>
          <p className="text-xl text-lime-100 mb-8 max-w-2xl mx-auto">
            Conhecer as isenções pode fazer grande diferença no rendimento final.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/investimentos"
              className="btn bg-white text-lime-600 hover:bg-gray-100"
            >
              Ver Investimentos
            </a>
            <a
              href="/calculadoras"
              className="btn bg-transparent text-white border-2 border-white hover:bg-white hover:text-lime-600"
            >
              Calcular Rendimento Líquido
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TaxExemptions;
